import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import CustomBreadcrumbs from "../../components-new/common/CustomBreadCrums";
import { PageTitle } from "../../components-new/common";
import CurrentExecutionTable from "./CurrentExecutionTable";
import {
  useCurrentExecutionQuery,
  useProductQuery,
  useModuleQuery,
  useTestCaseQuery,
} from "../../useQuery";
import AutocompleteComponent from "../../components-new/common/AutoCompleteComponent";

const BROWSER_OPTIONS = ["All", "Chrome", "Firefox", "Edge", "Safari"];
const STATUS_OPTIONS = ["Pass", "Fail", "Aborted"];

const INITIAL_FILTERS = {
  fromDate: "",
  toDate: "",
  runBy: [],
  browser: "All",
  status: [],
  application: "All",
  module: [],
  testCase: null,
};

const CurrentExecutionsPage = () => {
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [runByOptions, setRunByOptions] = useState([]);
  const [testCaseSearchKeyword, setTestCaseSearchKeyword] = useState("");
  const [debouncedSearchKeyword, setDebouncedSearchKeyword] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchKeyword(testCaseSearchKeyword), 500);
    return () => clearTimeout(handler);
  }, [testCaseSearchKeyword]);

  const { data: products = [] } = useProductQuery();
  const { data: modules = [] } = useModuleQuery(
    filters.application !== "All" ? filters.application : null,
    { enabled: filters.application !== "All" },
  );
  const { data: testCases = [], isLoading: isLoadingTestCase } = useTestCaseQuery(
    filters.module.length > 0 ? filters.module[0] : null,
    debouncedSearchKeyword,
    { enabled: filters.module.length > 0 },
  );
  const {
    data: currentExecutionData,
    refetch,
    isLoading,
    isFetching,
  } = useCurrentExecutionQuery(filters);

  useEffect(() => {
    const opts = currentExecutionData?.filterOptions?.runBy;
    if (Array.isArray(opts) && opts.length > 0) {
      setRunByOptions(opts);
    }
  }, [currentExecutionData]);

  const handleFilterChange = (key) => (event) => {
    const value = event.target.value;
    setFilters((prev) => {
      if (key === "application") {
        return { ...prev, application: value, module: [], testCase: null };
      }
      if (key === "module") {
        return { ...prev, module: value, testCase: null };
      }
      return { ...prev, [key]: value };
    });
    if (key === "application" || key === "module") {
      setTestCaseSearchKeyword("");
    }
  };

  const handleTestCaseChange = (event, newValue, reason) => {
    if (reason === "selectOption" && newValue && typeof newValue === "object") {
      setFilters((prev) => ({ ...prev, testCase: newValue.idtest_case }));
      setTestCaseSearchKeyword(newValue.Test_Case || "");
    } else if (reason === "clear" || (reason === "selectOption" && !newValue)) {
      setFilters((prev) => ({ ...prev, testCase: null }));
      setTestCaseSearchKeyword("");
    }
  };

  const handleTestCaseInputChange = (event, value, reason) => {
    setTestCaseSearchKeyword(value);
    if (reason === "input" && filters.testCase) {
      setFilters((prev) => ({ ...prev, testCase: null }));
    }
  };

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setTestCaseSearchKeyword("");
  };

  const handleConcurrentExecutionClick = (item) => {
    sessionStorage.setItem("logFileFromExeTestCaseReports", item.logfile);
    const url = `/new/reports?tcrid=${item.idtest_case_results}&tcname=${item.testcase}&tcRundate=${item.Executed_Date}`;
    window.open(url, "_blank");
  };

  return (
    <Box sx={{ my: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <PageTitle title="Current Executions" variant="h5" mb={1} />
          <CustomBreadcrumbs ariaLabel="breadcrumb">
            <Typography color="text.primary">Execution Reports</Typography>
            <Typography color="text.primary">Current Executions</Typography>
          </CustomBreadcrumbs>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
          disabled={isFetching}
        >
          Refresh
        </Button>
      </Box>

      <Box display="flex" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
        <TextField
          label="From Date"
          type="date"
          size="small"
          value={filters.fromDate}
          onChange={handleFilterChange("fromDate")}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />
        <TextField
          label="To Date"
          type="date"
          size="small"
          value={filters.toDate}
          onChange={handleFilterChange("toDate")}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Run By</InputLabel>
          <Select
            label="Run By"
            multiple
            value={filters.runBy}
            onChange={handleFilterChange("runBy")}
            renderValue={(selected) => (selected.length ? selected.join(", ") : "All")}
          >
            {runByOptions.map((email) => (
              <MenuItem key={email} value={email}>
                <Checkbox checked={filters.runBy.indexOf(email) > -1} />
                <ListItemText primary={email} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Browser</InputLabel>
          <Select label="Browser" value={filters.browser} onChange={handleFilterChange("browser")}>
            {BROWSER_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            multiple
            value={filters.status}
            onChange={handleFilterChange("status")}
            renderValue={(selected) => (selected.length ? selected.join(", ") : "All")}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                <Checkbox checked={filters.status.indexOf(opt) > -1} />
                <ListItemText primary={opt} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Application</InputLabel>
          <Select
            label="Application"
            value={filters.application}
            onChange={handleFilterChange("application")}
          >
            <MenuItem value="All">All</MenuItem>
            {products.map((app) => (
              <MenuItem key={app.idproducts} value={app.idproducts}>
                {app.productname}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Module</InputLabel>
          <Select
            label="Module"
            multiple
            value={filters.module}
            onChange={handleFilterChange("module")}
            disabled={filters.application === "All"}
            renderValue={(selected) =>
              selected.length
                ? selected
                    .map((id) => modules.find((m) => m.idmodules === id)?.modulename ?? id)
                    .join(", ")
                : "All"
            }
          >
            {modules.map((mod) => (
              <MenuItem key={mod.idmodules} value={mod.idmodules}>
                <Checkbox checked={filters.module.indexOf(mod.idmodules) > -1} />
                <ListItemText primary={mod.modulename} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <AutocompleteComponent
          key={`autocomplete-${filters.module.join(",")}`}
          size="small"
          label="Test Case"
          sx={{ minWidth: 200, backgroundColor: "#ffffff" }}
          disabled={filters.module.length === 0}
          options={testCases?.length ? testCases : []}
          value={
            filters.testCase && testCases?.length
              ? (() => {
                  const selectedTC = testCases.find((tc) => tc.idtest_case === filters.testCase);
                  if (selectedTC && testCaseSearchKeyword === selectedTC.Test_Case) {
                    return selectedTC;
                  }
                  return null;
                })()
              : null
          }
          inputValue={testCaseSearchKeyword}
          onChange={handleTestCaseChange}
          isLoading={isLoadingTestCase}
          onInputChange={handleTestCaseInputChange}
          getOptionLabel={(tc) => tc.Test_Case ?? ""}
          placeholder="Search Test Cases"
        />

        <Button variant="text" onClick={handleClearFilters}>
          Clear Filter
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="120px">
          <Box display="flex" alignItems="center" columnGap={1.5}>
            <CircularProgress size={20} />
            <Typography color="#8089A8">Loading current executions...</Typography>
          </Box>
        </Box>
      ) : (
        <CurrentExecutionTable
          data={Array.isArray(currentExecutionData?.data) ? currentExecutionData.data : []}
          onRowClick={handleConcurrentExecutionClick}
        />
      )}
    </Box>
  );
};

export default CurrentExecutionsPage;
